import { useQueryClient } from '@tanstack/react-query';
import { isEmpty } from 'lodash';
import { DateTime } from 'luxon';
import { useEffect } from 'react';
import { useRecoilValue } from 'recoil';

import API from '@/api';
import { useToast } from '@/hooks/use-toast';
import { ConnectionQuery } from '@/queries/connection';
import { CLIENT_BASE_URL } from '@/constants';
import { contactUs } from '@/helpers';
import {
  AuthContainer,
  ClientContainer,
  PagesContainer,
  UserContainer,
} from '@/providers';
import { InsightsDateRange, useDestinations, useInsights } from '@/states';
import { useRefreshSession } from '@/states/common';

const useGetPages = () => {
  const queryClient = useQueryClient();
  const { updatePages } = PagesContainer.useContainer();
  const { state: userState } = UserContainer.useContainer();
  const { updateClientAccounts } = ClientContainer.useContainer();
  const getPages = async (businessId = null) => {
    const query = {
      fields: 'provider',
      businessId: businessId ?? userState?.business?.id,
      paginate: false,
    };
    if (query.businessId) {
      const { queryKey, queryFn } = ConnectionQuery.QUERY_KEY.DATA_SOURCES(
        query.businessId
      );
      // Ensure the provider accounts are fetched and not stale
      const accounts = await queryClient.fetchQuery({
        queryKey,
        queryFn,
      });
      if (accounts) {
        updatePages(accounts);
        updateClientAccounts(accounts);
      }
    }
  };

  return getPages;
};

const usePatchPageClient = () => {
  const { updatePage } = PagesContainer.useContainer();
  const { state: userState } = UserContainer.useContainer();
  const invalidateDataSources = ConnectionQuery.useInvalidateDataSources();

  const patchPageClient = async (accountId, clientId, query, subAccountId) => {
    const response = await API.accounts.update(
      accountId,
      {
        subAccountId,
        clientId,
        businessId: userState?.business?.id,
      },
      query
    );
    updatePage({
      service: response.provider.type,
      ...response,
    });

    // Invalidate data sources cache after updating account
    if (userState?.business?.id) {
      await invalidateDataSources(userState.business.id);
    }

    return response;
  };

  return patchPageClient;
};

// helper open was opening multi times
// to avoid using this (useState was also not working)
let helperOpen = false;

const useConnectProvider = () => {
  const { state: authenticationState } = AuthContainer.useContainer();
  const { state: userState } = UserContainer.useContainer();
  const { state: clientState } = ClientContainer.useContainer();
  const dateRange = useRecoilValue(InsightsDateRange);
  const { getSingleSourceInsights } = useInsights();
  const { toast } = useToast();
  const refreshSession = useRefreshSession();

  const { getDestinations } = useDestinations();
  const invalidateDestinations = ConnectionQuery.useInvalidateDestinations();
  const invalidateDataSources = ConnectionQuery.useInvalidateDataSources();
  const queryClient = useQueryClient();

  const getPages = useGetPages();

  const receiveAuthStatus = event => {
    if (
      event.origin !== CLIENT_BASE_URL ||
      event.data?.type !== 'social-auth'
    ) {
      return;
    }

    if (event.data?.denied) {
      return;
    }

    if (event.data.beacon && !helperOpen) {
      helperOpen = true;
      contactUs();
    }

    if (event.data.auth === 'success') {
      getPages();
      toast({
        variant: 'success',
        title: 'Connected to provider',
      });
    } else if (event.data.auth === 'error') {
      toast({
        variant: 'destructive',
        title: 'Could not connect to provider',
      });
    }
  };

  // is probably redundant but not sure, window.close below does something similar
  useEffect(() => {
    window.addEventListener('message', receiveAuthStatus);
    return () => {
      window.removeEventListener('message', receiveAuthStatus);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const connectWithProvider = (service, datasource = {}) => {
    return new Promise((resolve, reject) => {
      let provider;
      let apiUrl = API.serverUrl;
      let serviceUrl = 'provider-oauth';
      switch (service) {
        case 'facebook':
        case 'facebookAds':
          provider = 'facebook';
          break;
        case 'instagram': {
          serviceUrl = 'datasources-auth';
          provider = service;
          break;
        }
        case 'linkedin':
          provider = 'linkedin2';
          break;
        case 'mailchimp':
          // Mailchimp does not allow `localhost` in their callback so we have to use `127.0.0.1`
          apiUrl = apiUrl.replace('localhost', '127.0.0.1');
          provider = service;
          break;
        case 'googleAnalytics4':
          provider = 'googleAnalytics';
          break;
        default:
          provider = service;
      }
      helperOpen = false;
      const businessId = userState?.business?.id;
      const authUrl = `${apiUrl}/${serviceUrl}/${provider}/?feathers_token=${authenticationState.accessToken}&businessId=${businessId}`;
      const authWindow = window.open(
        authUrl,
        'dataSourceWindow',
        'fullscreen=yes'
      );

      if (!authWindow) {
        reject(new Error('Failed to open authentication window'));
        return;
      }

      const { start, end } = dateRange ?? {};
      const startDate = DateTime.fromISO(start).toFormat('yyyy-MM-dd');
      const endDate = DateTime.fromISO(end).toFormat('yyyy-MM-dd');
      const clientId = clientState.selectedClient?.id;

      const timer = setInterval(() => {
        if (authWindow?.closed) {
          clearInterval(timer);

          // Invalidate data sources cache BEFORE fetching to ensure fresh data
          invalidateDataSources(businessId).then(() => {
            getPages();
          });

          if (!isEmpty(datasource)) {
            getSingleSourceInsights({
              sourceAccountId: datasource.id,
              isSubAccount: !!datasource.subAccount,
              clientId,
              service,
              startDate,
              endDate,
            });
          }

          if (service === 'googleDrive') {
            getDestinations();
            invalidateDestinations();
          }

          // Invalidate insights queries to pull latest data after reconnection
          queryClient.invalidateQueries({
            queryKey: ['insights'],
            exact: false,
          });
          refreshSession();

          resolve();
        }
      }, 1000);
    });
  };

  return connectWithProvider;
};

const useDeletePage = () => {
  const { deletePage } = PagesContainer.useContainer();
  const { state: userState } = UserContainer.useContainer();

  const patchPageClient = async accountId => {
    const response = await API.accounts.delete(accountId, {
      businessId: userState?.business?.id,
    });
    deletePage(response?.id);
  };

  return patchPageClient;
};

export { useConnectProvider, useDeletePage, useGetPages, usePatchPageClient };
